import { useMemo } from 'react'
import {
  useAllAccountSummary,
  useAllProfile,
  useCreditAccounts
} from 'const/syfApiQueries'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import { useSetAtom } from 'jotai'
import i18n from 'strings/i18n'
import { ClickAnalytics } from 'typings/analytics'
import { DATA_ANALYTICS, DATA_OBJECTS } from 'const/analytics'
import getAccountsForAnnualIncomeUpdate from 'helpers/getAccountsForAnnualIncomeUpdate'
import getAccountsForPostOfficeReturns from 'helpers/getAccountsForPostOfficeReturn'
import { NotificationAccount, NotificationList } from 'typings/notifications'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import getAccountsForEmailUpdate from 'helpers/getAccountsForEmailUpdate'
import useProfileNavigate from './useProfileNavigate'

const NOTIFICATION_CENTER = 'notification center'

export const UPDATE_INCOME_CLICK_ANALYTICS: ClickAnalytics = {
  ['data-reason']: 'update your income',
  ['data-type']: NOTIFICATION_CENTER,
  ['data-object']: DATA_OBJECTS.LINK,
  ['data-analytics']: DATA_ANALYTICS.CLICK
} as const

export const UPDATE_ADDRESS_CLICK_ANALYTICS: ClickAnalytics = {
  ['data-reason']: 'update mailing address',
  ['data-type']: NOTIFICATION_CENTER,
  ['data-object']: DATA_OBJECTS.LINK,
  ['data-analytics']: DATA_ANALYTICS.CLICK
} as const

export const UPDATE_EMAIL_CLICK_ANALYTICS: ClickAnalytics = {
  ['data-reason']: 'review email address',
  ['data-type']: NOTIFICATION_CENTER,
  ['data-object']: DATA_OBJECTS.LINK,
  ['data-analytics']: DATA_ANALYTICS.CLICK
} as const

const useNotificationCenter = () => {
  const {
    data: { accounts = [] } = {},
    isSuccess: isCreditAccountsAPISuccess
  } = useCreditAccounts()
  const updateModalStatus = useSetAtom(modalStatusAtom)
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const { handleProfileClick } = useProfileNavigate()

  const allAccountSummaryResults = useAllAccountSummary(accounts)
  const successfulAccountSummaries = allAccountSummaryResults
    .filter(({ data, isSuccess }) => isSuccess && data)
    .map(({ data }) => data)

  const allProfileResults = useAllProfile(accounts)
  const successfulProfiles = allProfileResults
    .filter(({ data, isSuccess }) => isSuccess && data)
    .map(({ data }) => data)

  const notificationList: NotificationList = useMemo(() => {
    const hasAccountSummaryData = successfulAccountSummaries.length > 0
    const hasProfileData = successfulProfiles.length > 0

    let accountsForPostOfficeReturn: NotificationAccount[]
    let accountsForAnnualIncomeUpdate: NotificationAccount[]
    let accountsForEmailUpdate: NotificationAccount[]

    if (hasAccountSummaryData) {
      accountsForPostOfficeReturn = getAccountsForPostOfficeReturns(
        successfulAccountSummaries
      )
    }

    if (hasProfileData) {
      accountsForEmailUpdate = getAccountsForEmailUpdate(successfulProfiles)
    }

    if (hasAccountSummaryData && hasProfileData) {
      accountsForAnnualIncomeUpdate = getAccountsForAnnualIncomeUpdate(
        successfulProfiles,
        successfulAccountSummaries
      )
    }
    /**
     * isAnnualIncomeNotificationEnabled - This flag is specific to the annual income notifications.
     * In the future there would be more notification type's, and should have similar flags for the same.
     */
    const showAnnualIncomeNotification =
      isFeatureFlagEnabled('isAnnualIncomeNotificationEnabled') &&
      accountsForAnnualIncomeUpdate?.length > 0

    const showPostOfficeReturnNotification =
      isFeatureFlagEnabled('isPostOfficeReturnNotificationEnabled') &&
      accountsForPostOfficeReturn?.length > 0

    const showEmailBounceUpdateNotification =
      isFeatureFlagEnabled('isEmailBounceNotificationEnabled') &&
      accountsForEmailUpdate?.length > 0

    const handleUpdateIncomeClick = () => {
      const { accountId } = accountsForAnnualIncomeUpdate[0]
      setSelectedAccount(accountId)
      updateModalStatus('UpdateIncome')
    }

    const handleUpdateAddressClick = () => {
      const { accountId } = accountsForPostOfficeReturn[0]
      handleProfileClick(accountId)
    }

    const handleUpdateEmailClick = () => {
      const { accountId } = accountsForEmailUpdate[0]
      handleProfileClick(accountId)
    }

    return {
      annualIncomeUpdate: {
        id: 'annual-income-notification',
        accountList: accountsForAnnualIncomeUpdate,
        title: i18n({
          notificationCenter: 'annualNetIncomeTitle'
        }) as string,
        description: i18n({
          notificationCenter: 'annualNetIncomeDescription'
        }) as string,
        linkTitle: i18n({
          notificationCenter: 'updateIncomeLinkTitle'
        }) as string,
        clickAnalytics: UPDATE_INCOME_CLICK_ANALYTICS,
        toBeShown: showAnnualIncomeNotification,
        handleClickHandler: handleUpdateIncomeClick
      },
      postOfficeReturn: {
        id: 'post-office-return-notification',
        accountList: accountsForPostOfficeReturn,
        clickAnalytics: UPDATE_ADDRESS_CLICK_ANALYTICS,
        title: i18n({
          notificationCenter: 'postOfficeReturnTitle'
        }) as string,
        description: i18n({
          notificationCenter: 'postOfficeReturnDescription'
        }) as string,
        linkTitle: i18n({
          notificationCenter: 'updateMailAddressLinkTitle'
        }) as string,
        toBeShown: showPostOfficeReturnNotification,
        handleClickHandler: handleUpdateAddressClick
      },
      emailUpdate: {
        id: 'email-update-notification',
        accountList: accountsForEmailUpdate,
        clickAnalytics: UPDATE_EMAIL_CLICK_ANALYTICS,
        title: i18n({
          notificationCenter: 'updateEmailTitle'
        }) as string,
        description: i18n({
          notificationCenter: 'updateEmailDescription'
        }) as string,
        linkTitle: i18n({
          notificationCenter: 'updateEmailLinkTitle'
        }) as string,
        toBeShown: showEmailBounceUpdateNotification,
        handleClickHandler: handleUpdateEmailClick
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successfulAccountSummaries, successfulProfiles])

  const { annualIncomeUpdate, postOfficeReturn, emailUpdate } = notificationList

  const shouldShowNotificationCenter =
    isCreditAccountsAPISuccess &&
    (annualIncomeUpdate.toBeShown ||
      postOfficeReturn.toBeShown ||
      emailUpdate.toBeShown)

  return {
    notificationList,
    shouldShowNotificationCenter
  }
}

export default useNotificationCenter
